/**
 * WebGL-Diagnose. WICHTIG: Diese Funktionen sind KEIN Gate mehr für den Viewer-
 * Mount. Sie werden nur noch für die kleine Status-Pille im Header und für den
 * `?diag=1`-Debug-Bar genutzt.
 *
 * Erkenntnis aus User-Report (Windows Chrome mit Nvidia GPU): Detached-Canvas-
 * WebGL-Tests produzieren false negatives. Deshalb hängen wir den Test-Canvas
 * jetzt kurzzeitig ans DOM an.
 */

type WebGLProbe = {
    webgl2: boolean;
    webgl: boolean;
    experimentalWebgl: boolean;
    renderer?: string;
    vendor?: string;
};

const ATTRS: WebGLContextAttributes = {
    failIfMajorPerformanceCaveat: false,
    powerPreference: "default",
};

function withAttachedCanvas<T>(fn: (canvas: HTMLCanvasElement) => T): T | null {
    if (typeof document === "undefined") return null;
    const canvas = document.createElement("canvas");
    canvas.width = 2;
    canvas.height = 2;
    canvas.style.position = "absolute";
    canvas.style.width = "2px";
    canvas.style.height = "2px";
    canvas.style.left = "-9999px";
    canvas.style.top = "-9999px";
    canvas.style.opacity = "0";
    canvas.style.pointerEvents = "none";
    const host = document.body || document.documentElement;
    let attached = false;
    try {
        if (host) {
            host.appendChild(canvas);
            attached = true;
        }
        return fn(canvas);
    } finally {
        if (attached && canvas.parentNode) {
            try {
                canvas.parentNode.removeChild(canvas);
            } catch {
                /* ignore */
            }
        }
    }
}

/**
 * Detaillierte Probe. Prüft webgl2, webgl, experimental-webgl separat und
 * versucht Renderer/Vendor via WEBGL_debug_renderer_info zu lesen.
 */
export function probeWebGL(): WebGLProbe {
    const result: WebGLProbe = {
        webgl2: false,
        webgl: false,
        experimentalWebgl: false,
    };
    if (typeof window === "undefined") return result;

    withAttachedCanvas((canvas) => {
        try {
            const gl2 = canvas.getContext(
                "webgl2",
                ATTRS,
            ) as WebGL2RenderingContext | null;
            if (gl2) {
                result.webgl2 = true;
                const dbg = gl2.getExtension("WEBGL_debug_renderer_info");
                if (dbg) {
                    result.renderer =
                        (gl2.getParameter(
                            dbg.UNMASKED_RENDERER_WEBGL,
                        ) as string) || undefined;
                    result.vendor =
                        (gl2.getParameter(
                            dbg.UNMASKED_VENDOR_WEBGL,
                        ) as string) || undefined;
                }
                gl2.getExtension("WEBGL_lose_context")?.loseContext();
            }
        } catch {
            /* ignore */
        }
    });

    withAttachedCanvas((canvas) => {
        try {
            const gl = canvas.getContext(
                "webgl",
                ATTRS,
            ) as WebGLRenderingContext | null;
            if (gl) {
                result.webgl = true;
                if (!result.renderer) {
                    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
                    if (dbg) {
                        result.renderer =
                            (gl.getParameter(
                                dbg.UNMASKED_RENDERER_WEBGL,
                            ) as string) || undefined;
                        result.vendor =
                            (gl.getParameter(
                                dbg.UNMASKED_VENDOR_WEBGL,
                            ) as string) || undefined;
                    }
                }
                gl.getExtension("WEBGL_lose_context")?.loseContext();
            }
        } catch {
            /* ignore */
        }
    });

    withAttachedCanvas((canvas) => {
        try {
            const gl = canvas.getContext(
                "experimental-webgl",
                ATTRS,
            ) as WebGLRenderingContext | null;
            if (gl) {
                result.experimentalWebgl = true;
                gl.getExtension("WEBGL_lose_context")?.loseContext();
            }
        } catch {
            /* ignore */
        }
    });

    return result;
}

/** true wenn IRGENDEINE WebGL-Variante verfügbar ist. Nur für die Pille. */
export function isWebGLLikelyAvailable(): boolean {
    if (typeof window === "undefined") return false;
    const p = probeWebGL();
    return p.webgl2 || p.webgl || p.experimentalWebgl;
}

/**
 * Session-Cache für `hasWebGLSupport()`. Detection ist nicht kostenlos
 * (Canvas-Attach + Context-Create + LoseContext) — deshalb nur einmal
 * ausführen und danach das Ergebnis wiederverwenden.
 */
let _webglCache: boolean | null = null;

/**
 * Session-memoisierte Feature-Detection.
 *
 * Wird als **early guard** von Three.js-Viewern (`Video360Viewer`,
 * `Photo360Viewer`, `BeforeAfter360Viewer`) und `ChromaKeyVideo` aufgerufen,
 * BEVOR ein WebGL-Context erstellt wird. Wenn `false`, rendern die Viewer
 * einen statischen Poster-Fallback statt einen App-Crash zu riskieren.
 *
 * Unterschied zu `isWebGLLikelyAvailable`: das hier ist gecached (schneller
 * für den Hot-Path in Component-Render), die diagnose-Variante frisch pro
 * Aufruf (für den Debug-Bar wichtig).
 */
export function hasWebGLSupport(): boolean {
    if (_webglCache !== null) return _webglCache;
    _webglCache = isWebGLLikelyAvailable();
    return _webglCache;
}

/** Nur für Tests: Cache invalidieren. */
export function _resetWebGLSupportCache() {
    _webglCache = null;
}
